import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getRoles, setRolePermissions, getPermissionsGrouped } from '../api';
import type { RoleOut, PermissionsGroupedOut } from '../types';

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [groupedPerms, setGroupedPerms] = useState<PermissionsGroupedOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<RoleOut | null>(null);
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [permSearch, setPermSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, p] = await Promise.all([getRoles(), getPermissionsGrouped()]);
      // Sort roles alphabetically by name
      const sortedRoles = [...r].sort((a, b) => a.name.localeCompare(b.name));
      setRoles(sortedRoles);
      setGroupedPerms(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = async (role: RoleOut) => {
    setEditRole(role);
    setSelectedSlugs(new Set(role.permission_slugs));
    setPermSearch('');
  };

  const toggleSlug = (slug: string) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const saveRolePermissions = async () => {
    if (!editRole) return;
    setSaving(true);
    try {
      await setRolePermissions(editRole.id, { permission_slugs: Array.from(selectedSlugs) });
      setEditRole(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const filteredRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase();
    if (!term) return roles;
    const filtered = roles.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        (r.description ?? '').toLowerCase().includes(term) ||
        r.permission_slugs.some((s) => s.toLowerCase().includes(term))
    );
    // Maintain alphabetical order
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [roles, roleSearch]);

  const filteredGroupedPerms = useMemo(() => {
    const term = permSearch.trim().toLowerCase();
    if (!term) return groupedPerms;
    return groupedPerms
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter(
          (p) => p.slug.toLowerCase().includes(term) || g.module.toLowerCase().includes(term)
        ),
      }))
      .filter((g) => g.permissions.length > 0);
  }, [groupedPerms, permSearch]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search roles..."
        value={roleSearch}
        onChange={(e) => setRoleSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 384 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: 'text.secondary' } }}>
              <TableCell>Role</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Access / Permissions</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRoles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {role.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={role.user_count} size="small" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {role.permission_slugs.slice(0, 6).map((s) => (
                      <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                    ))}
                    {role.permission_slugs.length > 6 && (
                      <Chip label={`+${role.permission_slugs.length - 6}`} size="small" variant="outlined" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" color="text.secondary">
                    {role.description ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => openEdit(role)}
                    sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No roles match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(editRole)}
        onClose={() => setEditRole(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" component="span">
              Edit Permissions:{' '}
              <Box component="span" color="primary.main" sx={{ fontFamily: 'monospace' }}>
                {editRole?.name}
              </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedSlugs.size} of {groupedPerms.reduce((n, g) => n + g.permissions.length, 0)} permissions selected
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setEditRole(null)}>
            &times;
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search permissions..."
            value={permSearch}
            onChange={(e) => setPermSearch(e.target.value)}
            sx={{ mb: 2, bgcolor: 'background.paper' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredGroupedPerms.map((g) => {
            return (
              <Paper key={g.module} variant="outlined" sx={{ mb: 1.5, overflow: 'hidden', borderRadius: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    {g.module}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'background.paper' }}>
                  {g.permissions.map((p, idx) => (
                    <Box
                      key={p.id}
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        borderTop: idx > 0 ? 1 : 0,
                        borderColor: 'divider',
                        bgcolor: selectedSlugs.has(p.slug) ? 'primary.50' : 'transparent',
                        '&:hover': { bgcolor: selectedSlugs.has(p.slug) ? 'primary.50' : 'grey.50' },
                      }}
                    >
                      <Checkbox size="small" checked={selectedSlugs.has(p.slug)} onChange={() => toggleSlug(p.slug)} />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={selectedSlugs.has(p.slug) ? 'primary.main' : 'text.primary'}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {p.slug}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            );
          })}
          {filteredGroupedPerms.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center">
              No permissions match your search.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <InfoOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="caption" fontWeight={600}>
              Affects {editRole?.user_count ?? 0} assigned user{editRole?.user_count === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditRole(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveRolePermissions} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPage;
